// src/api/features/uploads/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type { UploadImageBody, UploadImageResult } from "./types";

/* =========================
 * Helpers (API envelope)
 * ========================= */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: unknown): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

/* =========================
 * BASE URL
 * ========================= */

/**
 * Backend route:
 *   POST /api/v1/uploads/image
 */
const BASE = `${PATHS.uploads.base}/image`;

/* =========================
 * POST — /uploads/image
 * ========================= */

/**
 * Upload a single image to S3 via the generic uploads API.
 *
 * - Sends multipart/form-data with a single "file" field.
 * - module + tag are sent as query params.
 * - Returns UploadImageResult.
 */
export async function uploadImage(
  body: UploadImageBody
): Promise<UploadImageResult> {
  const { file, module, tag } = body;

  if (!file) {
    throw new Error("uploadImage: 'file' is required.");
  }
  if (!module) {
    throw new Error("uploadImage: 'module' is required.");
  }
  if (!tag || !tag.trim()) {
    throw new Error("uploadImage: 'tag' is required.");
  }

  // Build form-data
  const formData = new FormData();
  formData.append("file", file);

  // Build final URL with query params
  const url = `${BASE}?module=${encodeURIComponent(module)}&tag=${encodeURIComponent(
    tag.trim()
  )}`;

  const res = await request<UploadImageResult | ApiEnvelope<UploadImageResult>>({
    method: "POST",
    url,
    data: formData,
  });

  return unwrap<UploadImageResult>(res);
}
